import React, { memo, useCallback, useMemo, useState } from 'react'
import Modal from '@mui/material/Modal'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { TeamMemberInviteDetails, TeamRole } from 'src/interfaces/team'
import { Typography } from '@mui/material'
import { DataRow } from 'src/components/shared/DataRow'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { AddMarkets } from 'src/components/markets/AddMarkets'
import { Market } from 'src/interfaces/market'
import { TeamMemberInviteDetailsActions } from './TeamMemberInviteDetailsActions'
import { colors } from 'src/theme'
import { Chip } from 'src/components/shared/Chip'
import { SelectTeamRole } from '../select-team-role/SelectTeamRole'
import { useDispatch, useSelector } from 'react-redux'
import { getMarkets } from 'src/selectors/market'
import { UpdateTeamMemberInviteAccess } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'

interface Props {
  teamMemberInviteDetails: TeamMemberInviteDetails
  onClose(): void
  onRefreshTeamMemberInviteDetails(): Promise<void>
  onRefreshTeamMemberInvites(): Promise<void>
}

const TeamMemberInviteDetailsModalComponent = ({
  teamMemberInviteDetails,
  onClose,
  onRefreshTeamMemberInviteDetails,
  onRefreshTeamMemberInvites,
}: Props) => {
  const dispatch = useDispatch()

  const markets = useSelector(getMarkets)

  const { hasPermission } = useCheckPermissions()

  const container = useMemo(() => document.getElementById('root'), [])

  const initialSelectedMarkets = useMemo((): Market[] => {
    return teamMemberInviteDetails.allMarkets ? markets : teamMemberInviteDetails.markets || []
  }, [teamMemberInviteDetails, markets])

  const [openModalId, setOpenModalId] = useState<string | null>(null)
  const [selectedMarkets, setSelectedMarkets] = useState<Market[]>(initialSelectedMarkets)

  const [selectedTeamRole, setSelectedTeamRole] = useState<TeamRole | null>(
    teamMemberInviteDetails.role,
  )
  const [isRoleUpdating, setIsRoleUpdating] = useState(false)

  const allMarketsSelected = useMemo(
    () => selectedMarkets.length >= markets.length,
    [markets, selectedMarkets],
  )

  const handleSelectAllMarkets = useCallback(async () => {
    try {
      setSelectedMarkets(markets)

      await createAsyncAction(
        dispatch,
        UpdateTeamMemberInviteAccess.request({
          teamMemberInviteId: teamMemberInviteDetails.id,
          marketIds: markets.map((item) => item.id),
          allMarkets: true,
        }),
      )

      showToast('All markets added successfully!', { variant: 'success' })

      await onRefreshTeamMemberInviteDetails()
    } catch (err) {
      setSelectedMarkets(selectedMarkets)
      handleApiError(err)
    }
  }, [markets, teamMemberInviteDetails.id])

  const handleSelectMarket = useCallback(
    async (newMarket: Market) => {
      try {
        const updatedMarkets = [...selectedMarkets, newMarket]
        const selectedMarkersIds = updatedMarkets.map((market) => market.id)
        const isAllMarkets = markets.length === updatedMarkets.length

        setSelectedMarkets(updatedMarkets)

        await createAsyncAction(
          dispatch,
          UpdateTeamMemberInviteAccess.request({
            teamMemberInviteId: teamMemberInviteDetails.id,
            marketIds: selectedMarkersIds,
            allMarkets: isAllMarkets,
          }),
        )

        showToast('Market added successfully!', { variant: 'success' })

        await onRefreshTeamMemberInviteDetails()
      } catch (err) {
        setSelectedMarkets(selectedMarkets)
        handleApiError(err)
      }
    },
    [selectedMarkets, teamMemberInviteDetails.id],
  )

  const handleRemoveMarket = useCallback(
    async (removedMarket: Market) => {
      try {
        const updatedMarkets = selectedMarkets.filter((market) => market.id !== removedMarket.id)
        const selectedMarketIds = updatedMarkets.map((market) => market.id)

        setSelectedMarkets(updatedMarkets)

        await createAsyncAction(
          dispatch,
          UpdateTeamMemberInviteAccess.request({
            teamMemberInviteId: teamMemberInviteDetails.id,
            marketIds: selectedMarketIds,
            allMarkets: false,
          }),
        )

        showToast('Market removed successfully!', { variant: 'success' })

        await onRefreshTeamMemberInviteDetails()
      } catch (err) {
        setSelectedMarkets(selectedMarkets)
        handleApiError(err)
      }
    },
    [selectedMarkets, teamMemberInviteDetails.id],
  )
  const handleTeamRoleSelect = useCallback(
    async (role: TeamRole) => {
      try {
        setIsRoleUpdating(true)

        setSelectedTeamRole(role)

        await createAsyncAction(
          dispatch,
          UpdateTeamMemberInviteAccess.request({
            teamMemberInviteId: teamMemberInviteDetails.id,
            teamRoleId: role.id,
          }),
        )

        await onRefreshTeamMemberInviteDetails()

        setOpenModalId(null)

        showToast('Role updated successfully!', { variant: 'success' })
      } catch (err) {
        setSelectedTeamRole(selectedTeamRole)
        handleApiError(err)
      } finally {
        setIsRoleUpdating(false)
      }
    },
    [teamMemberInviteDetails.id, selectedTeamRole],
  )

  return (
    <Modal
      container={container}
      open
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex flex-row w-full h-full`}
    >
      <>
        <div css={tw`w-[400px] h-full bg-white flex flex-col`}>
          <ModalHeader title="User details" onClose={onClose} className={tw`text-black h-[48px]`} />
          <div css={tw`max-w-[400px] flex-1 flex flex-col justify-start border-0 p-4`}>
            <Typography
              css={tw`pb-4 border-0 border-b border-solid border-b-[#EDEDED]`}
              variant="h2"
            >{`${teamMemberInviteDetails.firstName} ${teamMemberInviteDetails.lastName}`}</Typography>
            <DataRow
              title="Status"
              direction="row"
              component={
                <Chip
                  className={tw`bg-[#D8E5FF]`}
                  textColor={colors.COLORS_BLUE_500}
                  text="Invited"
                />
              }
            />
            <DataRow direction="row" title="Email" text={teamMemberInviteDetails.email} />
            <div css={tw`py-4 border-0 border-b border-solid border-[#EDEDED]`}>
              <SelectTeamRole
                selectedTeamRole={selectedTeamRole}
                handleTeamRoleSelect={handleTeamRoleSelect}
                modalId="team-details"
                openModalId={openModalId}
                setOpenModalId={setOpenModalId}
                isLoading={isRoleUpdating}
                disabled={!hasPermission(UserPermission.InvitesEdit)}
              />
            </div>
            <AddMarkets
              markets={selectedMarkets}
              className={tw`py-[14px] border-0 border-b border-solid border-b-[#EDEDED]`}
              onSelectMarket={handleSelectMarket}
              onRemoveMarket={handleRemoveMarket}
              onSelectAllMarkets={handleSelectAllMarkets}
              allMarketsSelected={allMarketsSelected}
              disabled={!hasPermission(UserPermission.InvitesEdit)}
            />
          </div>
          {hasPermission(UserPermission.InvitesCreate) && (
            <TeamMemberInviteDetailsActions
              teamMemberInviteDetails={teamMemberInviteDetails}
              onClose={onClose}
              onRefreshTeamMemberInvites={onRefreshTeamMemberInvites}
            />
          )}
        </div>
        <div id="team-details" css={tw`flex m-6 w-[440px] pointer-events-none`} />
      </>
    </Modal>
  )
}

export const TeamMemberInviteDetailsModal = memo(TeamMemberInviteDetailsModalComponent)
